import React from "react";
import { injectStyles } from "./inject-styles";

/* ------------------------------------------------------------------ */
/*  Toast system                                                      */
/*  - <ToastProvider> wraps the app                                   */
/*  - useToast() returns { toast } to imperatively add toasts         */
/*  - Auto-dismiss + manual close                                     */
/* ------------------------------------------------------------------ */

export type ToastVariant = "success" | "error" | "warning" | "info";

export interface ToastData {
  id: string;
  message: React.ReactNode;
  variant: ToastVariant;
  duration: number;
}

/* -- Styles -------------------------------------------------------- */

const css = /* css */ `
  @keyframes sf-toast-in {
    from { opacity: 0; transform: translateY(8px); }
    to   { opacity: 1; transform: translateY(0); }
  }

  @keyframes sf-toast-out {
    from { opacity: 1; transform: translateY(0); }
    to   { opacity: 0; transform: translateY(8px); }
  }

  .sf-toast-rack {
    position: fixed;
    bottom: var(--sf-space-5);
    right: var(--sf-space-5);
    display: flex;
    flex-direction: column-reverse;
    gap: var(--sf-space-2);
    z-index: 9999;
    pointer-events: none;
    max-width: 400px;
  }

  .sf-toast {
    display: flex;
    align-items: flex-start;
    gap: var(--sf-space-2);
    padding: var(--sf-space-3) var(--sf-space-4);
    border-radius: var(--sf-radius-md);
    border: 1px solid transparent;
    background: var(--sf-bg-elevated);
    box-shadow: var(--sf-shadow-lg);
    font-family: var(--sf-font-sans);
    font-size: var(--sf-text-sm);
    color: var(--sf-text);
    pointer-events: auto;
    animation: sf-toast-in 0.3s ease forwards;
    line-height: var(--sf-leading-normal);
  }

  .sf-toast--dismissing {
    animation: sf-toast-out 0.2s ease forwards;
  }

  .sf-toast__icon {
    flex-shrink: 0;
    display: flex;
    align-items: center;
    margin-top: 1px;
  }

  .sf-toast__content {
    flex: 1;
    min-width: 0;
  }

  .sf-toast__close {
    flex-shrink: 0;
    background: none;
    border: none;
    color: var(--sf-text-muted);
    cursor: pointer;
    padding: 2px;
    display: flex;
    align-items: center;
    border-radius: var(--sf-radius-sm);
    transition: color var(--sf-transition-fast), background var(--sf-transition-fast);
  }
  .sf-toast__close:hover { color: var(--sf-text); background: var(--sf-gray-100); }
  .sf-toast__close:focus-visible {
    outline: var(--sf-ring-width) solid var(--sf-ring-color);
    outline-offset: var(--sf-ring-offset);
  }

  /* Variant colors (left border accent) */
  .sf-toast--success { border-left: 3px solid var(--sf-success); }
  .sf-toast--success .sf-toast__icon { color: var(--sf-success); }

  .sf-toast--error { border-left: 3px solid var(--sf-error); }
  .sf-toast--error .sf-toast__icon { color: var(--sf-error); }

  .sf-toast--warning { border-left: 3px solid var(--sf-warning); }
  .sf-toast--warning .sf-toast__icon { color: var(--sf-warning); }

  .sf-toast--info { border-left: 3px solid var(--sf-info); }
  .sf-toast--info .sf-toast__icon { color: var(--sf-info); }

  .sf-toast__request-id {
    display: block;
    margin-top: 2px;
    font-size: 10px;
    font-family: var(--sf-font-mono);
    color: var(--sf-text-muted);
    cursor: pointer;
    opacity: 0.7;
  }
  .sf-toast__request-id:hover { opacity: 1; text-decoration: underline; }
`;

/* -- Icons per variant -------------------------------------------- */

function ToastIcon({ variant }: { variant: ToastVariant }) {
  const size = 16;
  switch (variant) {
    case "success":
      return (
        <svg width={size} height={size} viewBox="0 0 16 16" fill="currentColor" aria-hidden="true">
          <path d="M8 1a7 7 0 110 14A7 7 0 018 1zm3.03 4.97a.75.75 0 00-1.06 0L7 8.94 5.53 7.47a.75.75 0 10-1.06 1.06l2 2a.75.75 0 001.06 0l3.5-3.5a.75.75 0 000-1.06z" />
        </svg>
      );
    case "error":
      return (
        <svg width={size} height={size} viewBox="0 0 16 16" fill="currentColor" aria-hidden="true">
          <path d="M8 1a7 7 0 100 14A7 7 0 008 1zm2.03 4.97a.75.75 0 010 1.06L8.81 8.25l1.22 1.22a.75.75 0 11-1.06 1.06L7.75 9.31l-1.22 1.22a.75.75 0 11-1.06-1.06l1.22-1.22-1.22-1.22a.75.75 0 111.06-1.06l1.22 1.22 1.22-1.22a.75.75 0 011.06 0z" />
        </svg>
      );
    case "warning":
      return (
        <svg width={size} height={size} viewBox="0 0 16 16" fill="currentColor" aria-hidden="true">
          <path d="M8 1a7 7 0 100 14A7 7 0 008 1zm-.75 3.75a.75.75 0 011.5 0v3.5a.75.75 0 01-1.5 0v-3.5zM8 11a1 1 0 100 2 1 1 0 000-2z" />
        </svg>
      );
    case "info":
      return (
        <svg width={size} height={size} viewBox="0 0 16 16" fill="currentColor" aria-hidden="true">
          <path d="M8 1a7 7 0 100 14A7 7 0 008 1zm.75 3.5a.75.75 0 01-1.5 0 .75.75 0 011.5 0zM7.25 7a.75.75 0 011.5 0v3.5a.75.75 0 01-1.5 0V7z" />
        </svg>
      );
  }
}

/* -- Toast item ---------------------------------------------------- */

interface ToastItemProps {
  toast: ToastData;
  onDismiss: (id: string) => void;
}

function ToastItem({ toast, onDismiss }: ToastItemProps) {
  const [dismissing, setDismissing] = React.useState(false);

  // Auto-dismiss after duration
  React.useEffect(() => {
    if (toast.duration <= 0) return;
    const timer = setTimeout(() => {
      setDismissing(true);
    }, toast.duration);
    return () => clearTimeout(timer);
  }, [toast.duration, toast.id]);

  // Remove from DOM after exit animation (timeout-based, not animationend)
  React.useEffect(() => {
    if (!dismissing) return;
    const timer = setTimeout(() => {
      onDismiss(toast.id);
    }, 250);
    return () => clearTimeout(timer);
  }, [dismissing, onDismiss, toast.id]);

  const handleClose = () => {
    setDismissing(true);
  };

  return (
    <div
      className={`sf-toast sf-toast--${toast.variant}${dismissing ? " sf-toast--dismissing" : ""}`}
      role="status"
      aria-live="polite"
    >
      <span className="sf-toast__icon">
        <ToastIcon variant={toast.variant} />
      </span>
      <span className="sf-toast__content">{toast.message}</span>
      <button
        className="sf-toast__close"
        onClick={handleClose}
        aria-label="Dismiss"
        type="button"
      >
        <svg width="14" height="14" viewBox="0 0 16 16" fill="currentColor" aria-hidden="true">
          <path d="M4.28 3.22a.75.75 0 00-1.06 1.06L6.94 8l-3.72 3.72a.75.75 0 101.06 1.06L8 9.06l3.72 3.72a.75.75 0 101.06-1.06L9.06 8l3.72-3.72a.75.75 0 00-1.06-1.06L8 6.94 4.28 3.22z" />
        </svg>
      </button>
    </div>
  );
}

/* -- Context + Provider ------------------------------------------- */

interface ToastContextValue {
  toast: (
    message: React.ReactNode,
    variant?: ToastVariant,
    duration?: number,
  ) => void;
}

const ToastContext = React.createContext<ToastContextValue | null>(null);

let toastCounter = 0;

export interface ToastProviderProps {
  children: React.ReactNode;
}

function ToastProvider({ children }: ToastProviderProps) {
  injectStyles("sf-toast", css);

  const [toasts, setToasts] = React.useState<ToastData[]>([]);

  const addToast = React.useCallback(
    (
      message: React.ReactNode,
      variant: ToastVariant = "info",
      duration = 4000,
    ) => {
      const id = `sf-toast-${++toastCounter}`;
      setToasts((prev) => [...prev, { id, message, variant, duration }]);
    },
    [],
  );

  const removeToast = React.useCallback((id: string) => {
    setToasts((prev) => prev.filter((t) => t.id !== id));
  }, []);

  const ctxValue = React.useMemo<ToastContextValue>(
    () => ({ toast: addToast }),
    [addToast],
  );

  return (
    <ToastContext.Provider value={ctxValue}>
      {children}
      <div className="sf-toast-rack" aria-label="Notifications">
        {toasts.map((t) => (
          <ToastItem key={t.id} toast={t} onDismiss={removeToast} />
        ))}
      </div>
    </ToastContext.Provider>
  );
}
ToastProvider.displayName = "ToastProvider";

/* -- Hook --------------------------------------------------------- */

function useToast(): ToastContextValue {
  const ctx = React.useContext(ToastContext);
  if (!ctx) {
    throw new Error("useToast must be used within a <ToastProvider>");
  }
  return ctx;
}

/* -- Toast message with optional request ID ------------------------------ */

interface ToastMessageProps {
  message: string;
  requestId?: string;
}

function ToastMessage({ message, requestId }: ToastMessageProps) {
  const handleCopy = () => {
    if (requestId) {
      navigator.clipboard.writeText(requestId).catch(() => {});
    }
  };

  return (
    <>
      {message}
      {requestId && (
        <span
          className="sf-toast__request-id"
          onClick={handleCopy}
          title="Click to copy request ID"
          role="button"
          tabIndex={0}
          onKeyDown={(e) => { if (e.key === "Enter") handleCopy(); }}
        >
          ID: {requestId}
        </span>
      )}
    </>
  );
}
ToastMessage.displayName = "ToastMessage";

export { ToastProvider, useToast, ToastItem, ToastMessage };
